import * as React from 'react'

import { Box, InfiniteScroll, Text } from '@factorialco/deprecated-design-system'
import {
  CreateCommentDocument,
  PostCommentsFragment,
  PostDataFragment,
  ShowPostDocument,
} from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import Separator from 'modules/core/components/Separator'
import { type EditorHandle } from 'modules/core/components/TextEditor'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import CommentComponent from './Comment'
import CommentInput from './Input'

interface Props {
  post: PostDataFragment & PostCommentsFragment
  fetchMoreComments: () => void
  hasMoreComments: boolean
  canManagePosts: boolean
}

const Comments = ({ post, fetchMoreComments, hasMoreComments, canManagePosts }: Props) => {
  const [commentContent, setCommentContent] = React.useState<string>('')
  const [usersIds, setUsersIds] = React.useState<number[]>([])
  const [createComment, { loading }] = useMutation(CreateCommentDocument, {
    extractorFn: (data) => data.postsMutations.createPostsComment,
    refetchQueries: [ShowPostDocument],
    awaitRefetchQueries: true,
  })
  const editorRef = React.useRef<EditorHandle>(null)
  const count = post.commentsCount.totalCount ?? 0

  return (
    <>
      <Box>
        <Text size="400" weight="semibold">
          {i18n.tp('posts.comments.count', {
            count,
          })}
        </Text>
        <Separator type="radical" />
      </Box>
      <Box>
        <CommentInput
          ref={editorRef}
          onSubmit={async () => {
            const result = handleResult(
              await createComment({
                variables: {
                  postId: post.id,
                  text: commentContent ?? '',
                  mentionedAccessIds: usersIds ?? [],
                },
              }),
              (_errors) => {}
            )

            track(trackingEvents.communities.writeCommentSubmit, {
              type: post.type,
              is_succesfull: result.success,
              error_code: false,
            })

            if (result.success) {
              setCommentContent('')
              editorRef.current?.clear()
              app.setFlash({
                type: 'correct',
                message: i18n.t('common.saved_successfully'),
              })
            }
          }}
          saving={loading}
          setComment={setCommentContent}
          comment={commentContent}
          setUsersIds={setUsersIds}
        />
        <InfiniteScroll
          pageStart={0}
          loadMore={fetchMoreComments}
          hasMore={hasMoreComments}
          initialLoad={false}
        >
          {[...post.commentsConnection.edges]
            .sort((commentA, commentB) =>
              moment(commentB.node.createdAt).diff(moment(commentA.node.createdAt))
            )
            .map(({ node: comment }) => (
              <CommentComponent
                key={comment.id}
                postId={post.id}
                comment={comment}
                usersIds={usersIds}
                setUsersIds={setUsersIds}
                canManagePosts={canManagePosts}
              />
            ))}
        </InfiniteScroll>
      </Box>
    </>
  )
}

export default observer(Comments)
